/* ===================================================================
   Rhody Reviews api/v1 -- Google connection domain (2026-07-07 onboarding
   rebuild). THE canonical connected-state definition + the confirm-listing
   call live here so every surface reads one source of truth.

   BACKEND CONTRACT (already live):
   Enum google_connection_status:
     'not_started' -> 'token_only' -> 'listing_pending_confirmation'
       -> 'confirmed_no_data' -> 'connected'
   SENDING (and every "Connected" render) is allowed ONLY at 'connected'
   (token valid + listing explicitly confirmed + >= 1 imported review).
   The legacy me.google_connected / google_location_known / a bare
   google_location_id MUST NOT drive a "Connected" render or the send gate
   anymore -- they are token/location signals only.
   =================================================================== */
import { request } from './_http';

/* The single derived connection state, computed server-side. */
export type GoogleConnectionStatus =
  | 'not_started'
  | 'token_only'
  | 'listing_pending_confirmation'
  | 'confirmed_no_data'
  | 'connected';

/* GET /business -> google_connection block. The canonical status plus the
   three primitives it is derived from, and the confirmed/candidate ids. */
export interface GoogleConnection {
  google_connection_status: GoogleConnectionStatus;
  oauth_token_valid: boolean;
  listing_confirmed: boolean;
  reviews_imported: number;
  confirmed_place_id: string | null;
  confirmed_at: string | null;
  confirmed_by: string | null;
  candidate_place_id: string | null;
}

/* A single review carried on the auto-selected candidate (for the newest-
   review sanity check on the confirm card). All fields optional/nullable so
   a thin backend payload never breaks typing. */
export interface GoogleCandidateReview {
  author?: string | null;
  rating?: number | null;
  text?: string | null;
  relative_time?: string | null;
}

/* GET /business -> google_candidate block. The listing returned by the
   owner's authenticated GBP account, awaiting an EXPLICIT confirm. NON-NULL
   ONLY when google_connection_status === 'listing_pending_confirmation'. */
export interface GoogleCandidate {
  place_id: string;
  name: string;
  address: string;
  category: string;
  rating: number;
  review_count: number;
  lat: number;
  lng: number;
  newest_review_snippet: string;
  reviews: GoogleCandidateReview[];
}

/* The listing echoed back by a successful confirm-listing (drives the
   reflect-back success screen -- rating + newest review from the CONFIRMED
   place, so a wrong match is caught instantly). */
export interface ConfirmedListing {
  name: string;
  address: string;
  category: string;
  rating: number;
  review_count: number;
  newest_review_snippet: string;
  lat: number;
  lng: number;
}

/* POST /api/v1/integrations/google/confirm-listing {place_id} -> 200. */
export interface ConfirmListingResult {
  ok: boolean;
  confirmed_place_id: string;
  confirmed_at: string;
  google_connection_status: GoogleConnectionStatus;
  reviews_imported: number;
  listing: ConfirmedListing;
  /* Optional import summary block (present when the confirm also ran a pull). */
  import?: unknown;
}

/* Single source of truth: a surface may render "Connected" (and the first
   real send may fire) ONLY when this returns true. Everything below renders
   the SAME single CTA: "Finish connecting Google." */
export function isGoogleConnected(
  status: GoogleConnectionStatus | undefined | null,
): boolean {
  return status === 'connected';
}

/* Read the canonical status defensively off a /me-shaped object. Structural
   typing (not a MeResponse import) avoids any module cycle. */
export function googleStatusFromMe(
  me: { google_connection_status?: GoogleConnectionStatus } | undefined | null,
): GoogleConnectionStatus {
  return me?.google_connection_status ?? 'not_started';
}

/* Read the canonical status defensively off a /business-shaped object. */
export function googleStatusFromBusiness(
  b:
    | { google_connection?: { google_connection_status?: GoogleConnectionStatus } }
    | undefined
    | null,
): GoogleConnectionStatus {
  return b?.google_connection?.google_connection_status ?? 'not_started';
}

/* POST /api/v1/integrations/google/connect-review-link -> connect by the owner's
   OWN pasted Google review link / Maps share URL / place_id. THE path for
   service-area / hidden-address listings Google Places text search cannot find
   (so name-search can never connect them). The pasted destination is stored
   directly and used as the review link; a place_id is resolved best-effort for
   review import + the reflect-back. */
export interface ConnectReviewLinkResult {
  ok: boolean;
  /* The stored review destination (normalized). */
  review_url: string;
  google_connection_status: GoogleConnectionStatus;
  /* True when we could NOT resolve a place_id (pure-link connect): the review
     link still works for sending, but reviews cannot be imported/monitored until
     the GBP API lists the owned listing. */
  link_only: boolean;
  /* Present only when a place_id resolved to real Google detail. */
  listing?: ConfirmedListing | null;
}

/* POST /api/v1/integrations/google/disconnect -> clears every column that pins
   the tenant to a Google listing (confirmed_review_url, confirmed_place_id,
   confirmed_at/by, google_location_id, gbp_location_id). Does NOT touch the
   business profile. Nulling gbp_location_id (the v4 reply-posting target) stops
   a post-disconnect re-pick from ever posting to the old listing. */
export interface DisconnectGoogleResult {
  client_id: string;
  disconnected: boolean;
  cleared: string[];
  detail: string;
}

/* One GBP location the owner's connected Google account manages. THIS is the
   fix for the repeated "find my business" failure: Google Places text search
   returns ZERO_RESULTS for a service-area business, but a listing the account
   OWNS is returned directly by the GBP API -- address or not -- so the owner
   picks theirs from an authoritative list instead of a guessed search. */
export interface GbpManagedLocation {
  location_id: string; // bare numeric id (v4 reply-posting target)
  resource_name: string; // "locations/12345"
  place_id: string; // metadata.placeId, "" for a pure SAB
  name: string; // listing title
  address: string; // "" for a service-area business (no storefront)
  is_service_area: boolean;
  maps_uri: string;
  new_review_uri: string; // the write-a-review link (used as the destination)
  primary_category: string;
  account: string;
}

/* GET /integrations/google/gbp-locations. reason is set (locations empty) when
   we could not enumerate: 'not_connected' (no OAuth token), 'gbp_not_approved'
   (403 for this account), or 'error' (transient). ok + empty + no reason means
   the account simply manages no listings. */
export interface GbpLocationsResult {
  ok: boolean;
  client_id: string;
  locations: GbpManagedLocation[];
  count?: number;
  reason?: 'not_connected' | 'gbp_not_approved' | 'error';
}

/* POST /integrations/google/confirm-gbp-location {location_id} -> 200. Confirms
   the location the owner picked from their managed list. Same reflect-back shape
   as confirm-listing so the success screen is shared. */
export interface ConfirmGbpLocationResult {
  ok: boolean;
  client_id: string;
  gbp_location_id: string;
  confirmed_place_id: string | null;
  confirmed_at: string | null;
  google_connection_status: GoogleConnectionStatus;
  reviews_imported: number;
  listing: ConfirmedListing;
  import?: unknown;
}

export const googleApi = {
  /* List the Google Business Profile locations the owner's connected account
     manages, for the "which of your businesses is this?" picker. The PRIMARY
     find-my-business path (Places text search is now the fallback). */
  listGbpLocations(signal?: AbortSignal): Promise<GbpLocationsResult> {
    return request<GbpLocationsResult>(
      '/api/v1/integrations/google/gbp-locations',
      { signal },
    );
  },

  /* Confirm the GBP location the owner picked from listGbpLocations(). Stores
     the numeric gbp_location_id (reply posting) + the place_id/review link
     (import + review destination). No Places ">=1 review" gate -- GBP ownership
     is the confirmation. */
  confirmGbpLocation(locationId: string): Promise<ConfirmGbpLocationResult> {
    return request<ConfirmGbpLocationResult>(
      '/api/v1/integrations/google/confirm-gbp-location',
      { method: 'POST', body: { location_id: locationId } },
    );
  },

  /* Explicitly affirm a specific Google place as THIS business. On 200 the
     backend saved confirmed_place_id + imported >= 1 review (bad-in-bad-out
     guard). A 422 (listing_validation_failed) means nothing was saved. */
  confirmListing(placeId: string): Promise<ConfirmListingResult> {
    return request<ConfirmListingResult>(
      '/api/v1/integrations/google/confirm-listing',
      { method: 'POST', body: { place_id: placeId } },
    );
  },

  /* Connect by the owner's OWN pasted Google review link / Maps URL / place_id.
     Bypasses Places search entirely -- works for hidden service-area listings. */
  connectReviewLink(reviewUrl: string): Promise<ConnectReviewLinkResult> {
    return request<ConnectReviewLinkResult>(
      '/api/v1/integrations/google/connect-review-link',
      { method: 'POST', body: { review_url: reviewUrl } },
    );
  },

  /* THE WAY OUT. Detach the tenant from whatever Google listing it is pinned to.
     A stored confirmed_review_url makes derive_status report CONNECTED
     unconditionally, and nothing else clears it -- so an owner who connected the
     wrong listing had no way back. Clears the connection ONLY; the business
     profile (name / address / phone / website) is left alone and stays editable
     in Settings. Idempotent. */
  disconnect(): Promise<DisconnectGoogleResult> {
    return request<DisconnectGoogleResult>(
      '/api/v1/integrations/google/disconnect',
      { method: 'POST', body: {} },
    );
  },
};
